// ============================================================================
// avatarStore.ts — SSoT аватаров пользователей (SRP).
// ЕДИНЫЙ источник dataURL аватара по id сущности (fileId → dataURL).
//   • getAvatar(uid, avatarUrl) — возвращает закэшированный dataURL, иначе
//     асинхронно качает файл (downloadFile) и сохраняет в кэш, затем notify.
//   • Каждый уникальный fileId качается один раз на всю сессию (кэш в памяти).
//   • Событие 'avatar:updated' — инвалидация кэша при смене аватара
//     (профиль → пере-рендер ВСЕХ мест, где рендерится аватар).
// Event-driven, без таймеров-поллинга. SSoT данных — сервер (files).
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { fileBase64 } from '@/lib/fileBytes';
import { createLogger } from '@/lib/logger';

const log = createLogger('Avatar');

// Кэш: fileId → dataURL (однократная загрузка на сессию)
const cache = new Map<string, string>();

// АКТУАЛЬНЫЙ fileId по uid (обновляется при смене аватара в профиле):
// если компонент передал устаревший fileId (списки сущностей загружены раньше),
// UserAvatar резолвит актуальный через getAvatarForUser.
const uidFileId = new Map<string, string>();

// Загрузки в процессе (fileId → обещание) — без гонок двойного качания
const inflight = new Map<string, Promise<string | null>>();

type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribeAvatars(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  for (const fn of listeners) fn();
}

/** Убрать аватар из кэша (смена/удаление аватара) + известить слушателей. */
export function invalidateAvatar(fileId: string): void {
  if (!fileId) return;
  cache.delete(fileId);
  inflight.delete(fileId);
  notify();
}

/**
 * Сменить аватар: перезаписать кэш готовым dataURL (из профиля, файл уже
 * загружен) + зарегистрировать актуальный fileId по uid + известить ВСЕ
 * точки отображения. Сервер уже обновлён.
 */
export function setAvatarDataUrl(uid: string, fileId: string, dataUrl: string): void {
  if (!fileId || !dataUrl) return;
  cache.set(fileId, dataUrl);
  if (uid) uidFileId.set(uid, fileId);
  inflight.delete(fileId);
  notify();
}

/**
 * Актуальный fileId пользователя (после смены аватара в профиле).
 * Иначе — переданный в компонент (загруженный из списка сущностей).
 */
export function resolveAvatarFileId(uid: string, fallbackFileId: string): string {
  const current = uidFileId.get(uid);
  return current || fallbackFileId || '';
}

async function loadAvatar(fileId: string): Promise<string | null> {
  // Пустой/несуществующий fileId — НЕ грузим и НЕ логируем (спам журнала
  // «avatar load failed fileId:<nil>» при каждом рендере без аватара).
  if (!fileId || fileId === 'undefined' || fileId === 'null' || fileId === '<nil>') return null;
  const existing = inflight.get(fileId);
  if (existing) return existing;
  const p = (async () => {
    try {
      const r: any = await getSDK().files.downloadFile({ id: fileId });
      const data = fileBase64(r?.data);
      if (!data) return null;
      const url = `data:${r?.fileType || 'image/jpeg'};base64,${data}`;
      cache.set(fileId, url);
      notify();
      return url;
    } catch (e) {
      log.warn('avatar load failed', { fileId, error: e });
      return null;
    } finally {
      inflight.delete(fileId);
    }
  })();
  inflight.set(fileId, p);
  return p;
}

/**
 * Получить dataURL аватара. Синхронно возвращает кэш (или null);
 * при первом обращении к новому fileId — качает в фоне и notify (пере-рендер).
 * fileId пуст/недоступен → null (рендерер показывает цветной круг по id).
 */
export function getAvatar(fileId: string): string | null {
  if (!fileId) return null;
  const cached = cache.get(fileId);
  if (cached !== undefined) return cached;
  void loadAvatar(fileId);
  return null;
}
